import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/icons';
import { useSession } from '../../app/session';
import { buttonClasses, type BadgeTone } from '../../components/ui';
import { ApiError, billingApi, type TenantInvoice, type TenantInvoices } from '../../lib/api';
import { formatCalendarDate, formatCalendarPeriod } from '../../lib/format';

/**
 * Invoices on the tenant side (D-107): a row per open invoice in the
 * Dashboard's "À traiter" queue while something is left to pay (D-186, it
 * replaced the dismissible notice), the "Facture" pill on Settings, and the
 * list on Plan & usage.
 *
 * A queue row goes away by itself once the back-office records the payment
 * (polled every few minutes and on window focus); it cannot be dismissed
 * (QUESTIONS Q22). The Settings pill stays until paid.
 * Agents get a 403 from the endpoint and simply see nothing: billing is the
 * owner's and admins' business (D-021).
 */

const POLL_MS = 5 * 60 * 1000;
export const INVOICES_ANCHOR = 'invoices';

/**
 * Keyed by workspace so switching workspaces never shows the previous one's
 * invoices; `data` is null on error (an agent's 403) rather than whatever an
 * earlier successful fetch left in the cache. Polling stops after an error —
 * an agent's 403 would otherwise come back every five minutes for the whole
 * session — and a 403 is final: only other errors are retried on focus. The
 * role is not consulted: the server's 403 already says who may read invoices,
 * and the request does not wait for `/workspaces/current` (which, since
 * D-183, answers while the workspace is suspended too).
 */
export function useInvoices(): { data: TenantInvoices | null } {
  const { session } = useSession();
  const query = useQuery({
    queryKey: ['billing', 'invoices', session?.activeWorkspaceId ?? null],
    queryFn: billingApi.invoices,
    enabled: Boolean(session?.activeWorkspaceId),
    retry: false,
    staleTime: 60_000,
    refetchInterval: (q) => (q.state.status === 'error' ? false : POLL_MS),
    refetchOnWindowFocus: (q) => !(q.state.error instanceof ApiError && q.state.error.status === 403),
  });
  return { data: query.isError ? null : (query.data ?? null) };
}

/** True while something is left to pay — drives the "Facture" pill on Settings and Plan & usage. */
export function useHasOpenInvoices(): boolean {
  return (useInvoices().data?.open.length ?? 0) > 0;
}

/**
 * "Facture" beside Settings and Plan & usage while an invoice is issued or
 * overdue (redesign Phase 3, A00-08, Q11): a word, where a bare dot left people
 * guessing between the invoice and the plan limits.
 */
export function InvoicePill({ onDark = false }: { onDark?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      title={t('nav.invoiceToPay')}
      className={`shrink-0 rounded-full px-[7px] text-[11px] font-semibold leading-[18px] ${
        onDark ? 'bg-(--color-sidebar-alert) text-(--color-sidebar-alert-text)' : 'bg-warning-50 text-warning-700'
      }`}
    >
      {t('nav.invoice')}
      <span className="sr-only"> · {t('nav.invoiceToPay')}</span>
    </span>
  );
}

const DISMISS_KEY = 'chatflow.invoice-notice.dismissed';

/**
 * Dropped when the session ends: a dismissal is the user's, not the browser's (D-134).
 * The dismissible notice and its writing of this key went with D-186; the key is
 * still cleared at session end for browsers that kept one (D-134).
 */
export function forgetInvoiceDismissals(): void {
  try {
    window.localStorage.removeItem(DISMISS_KEY);
  } catch {
    // Blocked storage held nothing to forget.
  }
}

/**
 * Downloads the invoice as a legal PDF. The accessible name follows the state
 * — "Téléchargement…", "indisponible" — rather than a fixed label that hid
 * both, and names the invoice; the server's reason (e.g. the issuer not
 * configured yet) is announced and shown on hover. `long` is the due line's
 * "Télécharger le PDF" (R35), `short` the invoices table's "PDF".
 */
export function PdfButton({ invoice, variant = 'long' }: { invoice: TenantInvoice; variant?: 'long' | 'short' }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = busy ? t('billing.downloading') : error ? t('billing.pdfUnavailable') : variant === 'long' ? t('billing.downloadPdf') : t('billing.pdf');

  async function download() {
    setBusy(true);
    setError(null);
    try {
      await billingApi.invoicePdf(invoice.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('billing.downloadFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        aria-busy={busy}
        aria-label={t('billing.pdfFor', { label, number: invoice.number })}
        title={error ?? undefined}
        className={`touch-target ${buttonClasses('secondary', 'sm')} shrink-0 whitespace-nowrap ${error ? 'text-danger-700' : ''}`}
      >
        <Icon name={error ? 'alert' : 'download'} className="size-[14px] shrink-0" />
        {label}
      </button>
      {error ? (
        <span role="alert" className="sr-only">
          {error}
        </span>
      ) : null}
    </>
  );
}

/** An invoice's status as a badge tone (R35): amber to pay, red overdue. The label is `billing.status.*`. */
export const INVOICE_TONE: Record<TenantInvoice['status'], BadgeTone> = {
  issued: 'warn',
  overdue: 'err',
  paid: 'ok',
  void: 'neutral',
};

/** "26 Oct 2026" / "26 oct. 2026", in UTC as the back office issues it. */
export function invoiceDay(iso: string | null): string {
  return iso ? formatCalendarDate(iso) : '—';
}

/** "14 Sept – 14 Oct" / "14 sept. – 14 oct.". */
export function invoicePeriod(invoice: TenantInvoice): string {
  return formatCalendarPeriod(invoice.periodStart, invoice.periodEnd);
}
